import { IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser'
import { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { HashRouter } from 'react-router-dom'
import { RecoilRoot } from 'recoil'
import App from './App'
import { msalConfig } from './authConfig'
import { accessTokenRequest } from './pages/Login'
import simUploadQueueStore from './services/simUploadQueueStore'
import { AccessTokenKey } from './utils/constants'
import reportWebVitals from './reportWebVitals'
import './styles/index.css'

import {
  AllCommunityModule as AgChartsAllCommunityModule,
  ModuleRegistry as AgChartsCommunityModuleRegistry
} from 'ag-charts-community'
import {
  BarSeriesModule as AgChartsBarSeriesModule,
  CategoryAxisModule as AgChartsCategoryAxisModule,
  AllEnterpriseModule as AgChartsAllEnterpriseModule,
  LicenseManager as AgChartsLicenseManager,
  LogAxisModule as AgChartsLogAxisModule,
  ModuleRegistry as AgChartsModuleRegistry,
  NumberAxisModule as AgChartsNumberAxisModule
} from 'ag-charts-enterprise'
import {
  AllEnterpriseModule as AgGridAllEnterpriseModule,
  LicenseManager as AgGridLicenseManager,
  ModuleRegistry as AgGridModuleRegistry
} from 'ag-grid-enterprise'

// Register modules
AgChartsCommunityModuleRegistry.registerModules([AgChartsAllCommunityModule])
AgChartsModuleRegistry.registerModules([
  AgChartsAllEnterpriseModule,
  AgChartsBarSeriesModule,
  AgChartsCategoryAxisModule,
  AgChartsLogAxisModule,
  AgChartsNumberAxisModule
])
AgGridModuleRegistry.registerModules([AgGridAllEnterpriseModule])

// Set license keys
AgChartsLicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE_KEY)
AgGridLicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE_KEY)

// Benign browser warning fired by chart/grid libraries on resize; keep it out of the dev overlay.
const RESIZE_OBSERVER_LOOP_MESSAGES = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications.'
]
const hideDevServerOverlay = () => {
  // webpack-dev-server renders its overlay via its own error listener we can't preempt, so hide it.
  ;['webpack-dev-server-client-overlay', 'webpack-dev-server-client-overlay-div'].forEach(id => {
    const el = document.getElementById(id)
    if (el) el.style.display = 'none'
  })
}
window.addEventListener(
  'error',
  event => {
    if (RESIZE_OBSERVER_LOOP_MESSAGES.includes(event.message)) {
      event.stopImmediatePropagation()
      event.preventDefault()
      hideDevServerOverlay()
      // The overlay element may be created after this handler runs, so hide it next frame too.
      requestAnimationFrame(hideDevServerOverlay)
    }
  },
  true
)

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
const msalInstance: IPublicClientApplication = new PublicClientApplication(msalConfig)

msalInstance.initialize().then(() => {
  simUploadQueueStore.setTokenRefresher(async () => {
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0]
    if (!account) return
    const res = await msalInstance.acquireTokenSilent({ ...accessTokenRequest, account })
    localStorage.setItem(AccessTokenKey, res.accessToken)
  })

  // Register Callbacks for Redirect flow
  msalInstance
    .handleRedirectPromise()
    .then(redirectResp => {
      console.log('MSAL Redirect Response: ', redirectResp)
      if (redirectResp !== null) {
        localStorage.setItem('ACCESS_TOKEN', redirectResp.accessToken)
        msalInstance.setActiveAccount(redirectResp.account)
      } else {
        // need to call getAccount here?
        const currentAccounts = msalInstance.getAllAccounts()
        if (!currentAccounts || currentAccounts.length < 1) {
          console.log('Need to login>>>')
          return msalInstance.loginRedirect(accessTokenRequest)
        }
      }
    })
    .catch(err => {
      console.error('MSAL Redirect Error: ', err)
    })

  /*
  See https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
  Need to acquire each API token in a seperate call.  Commenting the graph API for now as not needed.
   */

  root.render(
    <RecoilRoot>
      <HashRouter>
        <Suspense fallback={<div>Loading CFD Application...</div>}>
          <App pca={msalInstance} />
        </Suspense>
      </HashRouter>
    </RecoilRoot>
  )
})

reportWebVitals()
